import { Component, Input, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { SearchResult } from '../../interfaces/searchResult.interface';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-pdf',
  templateUrl: './pdf.component.html',
  styleUrls: ['./pdf.component.css'],
  standalone: true,
  imports: [CommonModule],
})
export class PdfComponent implements AfterViewInit {
  @Input() selectedDocument!: SearchResult;
  @ViewChild('printDoc', { static: false }) printDocRef!: ElementRef;

  ngAfterViewInit() {
    console.log('PDF component initialized', this.printDocRef);
  }

  // Helper method để convert timestamp thành Date
  private convertToDate(timestamp: any): Date {
    if (!timestamp) return new Date();
    
    if (timestamp && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    } else if (timestamp && typeof timestamp.seconds === 'number') {
      return new Date(timestamp.seconds * 1000);
    } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      return new Date(timestamp);
    } else if (timestamp instanceof Date) {
      return timestamp;
    } else {
      return new Date(String(timestamp));
    }
  }

  // Format ngày khám
  get formattedCreatedAt(): string {
    if (!this.selectedDocument?.createdAt) return '';
    
    try {
      const date = this.convertToDate(this.selectedDocument.createdAt);
      
      // Format: dd/MM/yyyy
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  }

  // Format ngày khám với giờ
  get formattedCreatedAtWithTime(): string {
    if (!this.selectedDocument?.createdAt) return '';
    
    try {
      const date = this.convertToDate(this.selectedDocument.createdAt);
      
      // Format: dd/MM/yyyy HH:mm
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
      console.error('Error formatting date with time:', error);
      return '';
    }
  }

  async createAndDownloadPDF() {
    if (!this.selectedDocument) {
      console.error('No document selected');
      return;
    }

    const element = this.printDocRef?.nativeElement;
    if (!element) {
      console.error('Print element not found');
      return;
    }

    console.log('Starting PDF generation...');

    try {
      element.classList.add('pdf-visible');
      element.classList.remove('pdf-hidden');

      await this.delay(300);

      console.log('Element dimensions:', {
        width: element.offsetWidth,
        height: element.offsetHeight,
        scrollWidth: element.scrollWidth,
        scrollHeight: element.scrollHeight
      });

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: true,
        width: element.scrollWidth,
        height: element.scrollHeight,
        x: 0,
        y: 0
      });

      console.log('Canvas created:', {
        width: canvas.width,
        height: canvas.height
      });

      const imgData = canvas.toDataURL('image/png', 1.0);
      
      if (imgData === 'data:,') {
        throw new Error('Canvas is empty');
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      console.log('PDF dimensions:', { pdfWidth, pdfHeight });

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      
      const fileName = `${this.selectedDocument?.fullName || 'medical'}_summary.pdf`;
      pdf.save(fileName);

      console.log('PDF saved successfully');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Có lỗi xảy ra khi tạo PDF. Vui lòng thử lại.');
    } finally {
      element.classList.remove('pdf-visible');
      element.classList.add('pdf-hidden');
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
